import styled from 'styled-components';
import theme from '../../pagesv2/theme';

export const Card = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
  font-family: ${theme.typography.fontFamily};
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.md};
`;

export const TitleGroup = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

export const Title = styled.h3`
  font-size: ${theme.typography.h3.size};
  font-weight: ${theme.typography.h3.weight};
  color: ${theme.colors.text};
  margin: 0;
`;

export const PeriodLabel = styled.span`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textSecondary};
`;

export const GearButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.textSecondary};
  cursor: pointer;
  font-size: 18px;
  line-height: 1;
  padding: ${theme.spacing.xs};
  border-radius: ${theme.radii.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primaryGhost};
    color: ${theme.colors.primary};
  }
`;

/* Scope selector (Entire company / a group) */
export const ScopeBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.md};
`;

export const ScopeSelect = styled.select`
  padding: 6px 10px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.typography.small.size};
  font-family: ${theme.typography.fontFamily};
  color: ${theme.colors.text};
  background: ${theme.colors.surface};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
  }
`;

/* League table */
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: ${theme.typography.body.size};
`;

export const HeadRow = styled.tr`
  border-bottom: 2px solid ${theme.colors.border};
`;

export const Th = styled.th`
  text-align: ${props => (props.$align || 'left')};
  font-size: ${theme.typography.tiny.size};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  white-space: nowrap;
  ${props => props.$divider && `border-left: 1px solid ${theme.colors.border};`}
`;

// Faint podium tints for the top three places.
const TOP3_TINT = {
  1: 'rgba(212, 160, 23, 0.12)',  // gold
  2: 'rgba(148, 157, 165, 0.14)', // silver
  3: 'rgba(176, 107, 58, 0.12)',  // bronze
};

// Row background precedence: top-3 tint > current user > zebra > none.
function rowBackground(props) {
  if (TOP3_TINT[props.$rank]) return TOP3_TINT[props.$rank];
  if (props.$isCurrentUser) return theme.colors.primaryGhost;
  if (props.$zebra) return theme.colors.surfaceHover;
  return 'transparent';
}

export const BodyRow = styled.tr`
  background: ${rowBackground};
  /* Current user keeps a left accent even when sitting on a podium tint. */
  box-shadow: ${props => (props.$isCurrentUser ? `inset 3px 0 0 ${theme.colors.primary}` : 'none')};
  transition: filter ${theme.transitions.fast};

  &:hover {
    filter: brightness(0.98);
  }
`;

export const PosCell = styled.td`
  width: 64px;
  text-align: center;
  padding: 10px ${theme.spacing.sm};
  font-size: ${theme.typography.body.size};
  font-weight: 700;
  color: ${props => {
    if (props.$rank === 1) return '#d4a017';
    if (props.$rank === 2) return '#8a939b';
    if (props.$rank === 3) return '#b06b3a';
    return theme.colors.textMuted;
  }};
`;

export const NameCell = styled.td`
  padding: 10px ${theme.spacing.sm};
`;

export const NameInner = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  min-width: 0;
`;

export const Avatar = styled.div`
  width: 28px;
  height: 28px;
  flex: none;
  border-radius: ${theme.radii.full};
  background: ${theme.colors.primary};
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${theme.typography.tiny.size};
  font-weight: 600;
`;

export const NameText = styled.span`
  font-weight: ${props => (props.$isCurrentUser ? 700 : 500)};
  color: ${theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const YouTag = styled.span`
  font-size: ${theme.typography.tiny.size};
  font-weight: 600;
  color: ${theme.colors.primary};
  background: ${theme.colors.primaryGhost};
  padding: 2px 6px;
  border-radius: ${theme.radii.sm};
`;

export const PointsCell = styled.td`
  width: 80px;
  text-align: right;
  padding: 10px ${theme.spacing.md};
  font-weight: 800;
  color: ${theme.colors.text};
  border-left: 1px solid ${theme.colors.border};
  white-space: nowrap;
`;

export const Empty = styled.div`
  padding: ${theme.spacing.lg};
  text-align: center;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.body.size};
`;

export const Footer = styled.div`
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.borderLight};
  display: flex;
  justify-content: center;
`;

export const OptInButton = styled.button`
  background: none;
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.primary};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  cursor: pointer;
  padding: 6px 14px;
  border-radius: ${theme.radii.sm};
  transition: all ${theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${theme.colors.primaryGhost};
    border-color: ${theme.colors.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

export const EnablePrompt = styled.div`
  text-align: center;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.body.size};

  p {
    margin: 0 0 ${theme.spacing.md} 0;
  }
`;

export const EnableButton = styled.button`
  background: ${theme.colors.primary};
  border: none;
  color: #fff;
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.small.size};
  font-weight: 600;
  cursor: pointer;
  padding: 8px 18px;
  border-radius: ${theme.radii.sm};
  transition: all ${theme.transitions.fast};

  &:hover {
    background: ${theme.colors.primaryDark};
  }
`;
